import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Input from '../../../../../shared/_TBComponents/Input';
import SelectBox from '../../../../../shared/_TBComponents/SelectBox';
import Text from '../../../../../shared/_TBComponents/Text';
import IconActionButton from '../IconActionButton';
import UserProfileFields, { Row, FieldWrapper } from './UserProfileFields';

const ContactCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px;
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  margin-bottom: 6px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
`;

const ContactInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContactActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ContactsContainer = styled.div`
  margin-top: 5px;
`;

const Separator = styled.div`
  align-self: stretch;
  background-color: #cccccc;
  width: 1.5px;
  box-sizing: border-box;
  margin: ${(props) => (props.isIconButtons ? '4px 4px' : '0')};
`;

const IntegrationCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 10px 10px;
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  height: 45px;
  width: 35%;

  &:focus-within {
    border-color: #77A7FF;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const IntegrationInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 12px;
`;

const IntegrationInputWrapper = styled.div`
  position: relative;
  flex: 1;

  input {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  input:focus {
    border: none !important;
    outline: none !important;
  }
`;

const AthleteProfileForm = ({
  athlete,
  contacts,
  groups,
  selectBoxStyles,
  onOpenNestedModal,
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  errors,
  touched,
  isNew,
  usernamesEnabled,
  scrollContainerRef,
}) => {
  const smartabaseEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.smartabaseEnabled,
  );

  return (
    <>
      <UserProfileFields
        athlete={athlete}
        isNew={isNew}
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        setFieldValue={setFieldValue}
        usernamesEnabled={usernamesEnabled}
        selectBoxStyles={selectBoxStyles}
        scrollContainerRef={scrollContainerRef}
        showPINFields
      />

      <Row>
        <FieldWrapper>
          <SelectBox
            title='Groups'
            isMulti
            options={groups.map(({ id, name }) => ({ id, name }))}
            value={values.groups}
            onChangeFunction={(opt) => setFieldValue('groups', opt)}
            placeholder='Select group'
            styledProps={{ border: `1px solid ${touched.groups && errors.groups ? '#CC0404' : '#EEEEEE'}` }}
            customStyles={selectBoxStyles}
            titleStyledProps={{ fontWeight: 700 }}
            menuPortalTarget={document.body}
            onMenuOpen={() => {
              const scrollContainer = scrollContainerRef?.current;
              if (scrollContainer) {
                scrollContainer.style.overflowY = 'hidden';
              }
            }}
            onMenuClose={() => {
              const scrollContainer = scrollContainerRef?.current;
              if (scrollContainer) {
                scrollContainer.style.overflowY = 'auto';
              }
            }}
          />
          {touched.groups && errors.groups && (
            <Text styledTextProps={{
              fontSize: '13px', fontWeight: 700, color: '#CC0404', marginTop: '5px',
            }}
            >
              {errors.groups}
            </Text>
          )}
        </FieldWrapper>
      </Row>

      <Row>
        <FieldWrapper>
          <Text styledWrapperProps={{ marginBottom: '4px', display: 'block', fontWeight: 700 }}>
            Parent/Guardian Contacts
            <Text styledTextProps={{ fontSize: '12px', color: '#9E9E9E' }}>
              Any contacts listed below will be eligible to receive Weekly Performance
              Assessment emails.
            </Text>
          </Text>
          {contacts && contacts.length > 0 && (
            <ContactsContainer>
              {contacts.map((contact, index) => (
                <ContactCard key={contact.id || index}>
                  <ContactInfo>
                    <Text styledTextProps={{ fontSize: '12px', fontWeight: 600 }}>
                      {contact.name}
                    </Text>
                    <Text styledTextProps={{ fontSize: '12px', color: '#757575' }}>
                      {contact.status ? `Status: ${contact.status} - ${contact.email}` : contact.email}
                    </Text>
                  </ContactInfo>
                  <ContactActions>
                    <IconActionButton
                      type='edit'
                      tabIndex={-1}
                      onClick={(e) => { e.preventDefault(); onOpenNestedModal('editContact', index); }}
                    />
                    <IconActionButton
                      type='delete'
                      tabIndex={-1}
                      onClick={(e) => { e.preventDefault(); onOpenNestedModal('deleteContact', index); }}
                    />
                  </ContactActions>
                </ContactCard>
              ))}
            </ContactsContainer>
          )}
          <Button
            name='Add Contact'
            icon='add-user'
            iconSize={16}
            iconColor='#FFFFFF'
            fontSize='14px'
            bgColor='#0DCC8A'
            customBorderRadius='4px'
            noBorder
            txtColor='#FFFFFF'
            styledTextProps={{ fontWeight: 600 }}
            styledWrapperProps={{ marginTop: '8px', alignSelf: 'flex-start', tabIndex: -1 }}
            onClick={(e) => { e.preventDefault(); onOpenNestedModal('addContact'); }}
          />
        </FieldWrapper>
      </Row>

      {smartabaseEnabled && (
        <Row>
          <FieldWrapper>
            <Text styledWrapperProps={{ marginBottom: '4px', fontWeight: 700 }}>
              Integrations
            </Text>
            <IntegrationCard>
              <Text
                styledWrapperProps={{ paddingRight: '10px' }}
                styledTextProps={{ fontSize: '14px', fontWeight: 600 }}
              >
                Smartabase ID
              </Text>
              <Separator />
              <IntegrationInfo>
                <IntegrationInputWrapper>
                  <Input
                    name='smartabaseId'
                    type='text'
                    inputOnly
                    value={values.smartabaseId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder='ex. 1234'
                    fontSize='14px'
                    width='100%'
                  />
                </IntegrationInputWrapper>
              </IntegrationInfo>
            </IntegrationCard>
          </FieldWrapper>
        </Row>
      )}
    </>
  );
};

AthleteProfileForm.propTypes = {
  athlete: PropTypes.shape({
    first: PropTypes.string,
    last: PropTypes.string,
    pic: PropTypes.string,
    email: PropTypes.string,
    ssoId: PropTypes.string,
    mobileCountry: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    mobileNumber: PropTypes.string,
    gender: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    externalId: PropTypes.string,
    groups: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    smartabaseId: PropTypes.string,
  }).isRequired,
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
  groups: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  selectBoxStyles: PropTypes.instanceOf(Object).isRequired,
  onOpenNestedModal: PropTypes.func.isRequired,
  values: PropTypes.shape({
    first: PropTypes.string,
    last: PropTypes.string,
    email: PropTypes.string,
    ssoId: PropTypes.string,
    mobileCountry: PropTypes.string,
    mobileNumber: PropTypes.string,
    gender: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    externalId: PropTypes.string,
    changePassword: PropTypes.string,
    confirmChangePassword: PropTypes.string,
    changePIN: PropTypes.string,
    confirmChangePIN: PropTypes.string,
    smartabaseId: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.instanceOf(Object),
  touched: PropTypes.instanceOf(Object),
  isNew: PropTypes.bool,
  usernamesEnabled: PropTypes.bool,
  scrollContainerRef: PropTypes.instanceOf(Object),
};

AthleteProfileForm.defaultProps = {
  contacts: [],
  groups: [],
  errors: {},
  touched: {},
  isNew: false,
  usernamesEnabled: false,
  scrollContainerRef: null,
};

export default AthleteProfileForm;
